import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Empty,
  Input,
  InputNumber,
  message,
  Row,
  Skeleton,
  Space,
  Switch,
  Tag,
  Typography,
} from "antd";
import instance from "../../store/actions/instance";

const { Paragraph, Text } = Typography;
const { Search } = Input;

const STATUS_COLORS = {
  active: "green",
  expired: "orange",
  inactive: "red",
  churned: "orange",
  live: "green",
  offline: "red",
};

const formatMoney = (value, currency = "QAR") => {
  const amount = Number(value || 0);
  if (Number.isInteger(amount)) {
    return `${amount} ${currency}`;
  }
  return `${amount.toFixed(2)} ${currency}`;
};

const formatDate = (value) => {
  if (!value) {
    return "N/A";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "N/A";
  }
  return date.toLocaleDateString();
};

const getAddonByKey = (store, key) =>
  (store.addons || []).find((addon) => addon.key === key) || null;
const hasUnlimitedBranches = (store) =>
  store?.branch_limit === 0 || store?.branch_limit === "0";
const hasUnlimitedProducts = (store) =>
  store?.limit_products === 0 || store?.limit_products === "0";
const PRODUCT_BLOCK_SIZE = 500;
const getAdditionalProductsAddon = (store) =>
  getAddonByKey(store, "additional_products") || {
    key: "additional_products",
    label: "Additional products",
    price: 15,
    currency: store.currency || "QAR",
    enabled: false,
    billed: false,
    quantity: 0,
    billed_quantity: 0,
    quantity_based: true,
    monthly_total: 0,
  };

const ManageSubscriptions = ({ authToken, onBack }) => {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionKey, setActionKey] = useState("");
  const [branchDrafts, setBranchDrafts] = useState({});
  const [productDrafts, setProductDrafts] = useState({});
  const [searchValue, setSearchValue] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);

  const syncPayload = (data) => {
    setPayload(data);
    const nextBranchDrafts = {};
    const nextProductDrafts = {};
    (data.stores || []).forEach((store) => {
      const branchesAddon = getAddonByKey(store, "additional_branches");
      const productsAddon = getAdditionalProductsAddon(store);
      nextBranchDrafts[store.id] = branchesAddon?.quantity || 0;
      nextProductDrafts[store.id] = productsAddon?.quantity || 0;
    });
    setBranchDrafts(nextBranchDrafts);
    setProductDrafts(nextProductDrafts);
  };

  const fetchStores = async () => {
    setLoading(true);
    try {
      const res = await instance.post("/fetch_enable_reseller_subscriptions/", {
        authToken,
      });
      if (res.data.success) {
        syncPayload(res.data);
      } else {
        message.error(res.data.message || "Could not fetch stores");
      }
    } catch (error) {
      message.error("Could not fetch stores");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchStores();
  }, [authToken]);

  const stores = useMemo(() => payload?.stores || [], [payload]);
  const activeStoresCount = useMemo(
    () => stores.filter((store) => store.status === "active").length,
    [stores],
  );
  const filteredStores = useMemo(() => {
    const scopedStores = activeOnly
      ? stores.filter((store) => store.status === "active")
      : stores;
    const normalizedQuery = searchValue.trim().toLowerCase();
    if (!normalizedQuery) {
      return scopedStores;
    }

    return scopedStores.filter((store) => {
      const name = String(store.name || "").toLowerCase();
      const domain = String(store.domain || "").toLowerCase();
      return (
        name.includes(normalizedQuery) || domain.includes(normalizedQuery)
      );
    });
  }, [activeOnly, searchValue, stores]);

  const submitStoreStatus = async (storeId, enabled) => {
    setActionKey(`subscription-${storeId}`);
    try {
      const res = await instance.post(
        "/update_enable_reseller_store_subscription/",
        {
          authToken,
          storeId,
          enabled,
        },
      );
      if (res.data.success) {
        syncPayload(res.data);
        message.success(enabled ? "Store enabled" : "Store disabled");
      } else {
        message.error(res.data.message || "Could not update store");
      }
    } catch (error) {
      message.error("Could not update store");
    }
    setActionKey("");
  };

  const submitAddon = async (storeId, addonKey, body) => {
    setActionKey(`${addonKey}-${storeId}`);
    try {
      const res = await instance.post("/update_enable_reseller_store_addon/", {
        authToken,
        storeId,
        addonType: addonKey,
        ...body,
      });
      if (res.data.success) {
        syncPayload(res.data);
        message.success("Store updated");
      } else {
        message.error(res.data.message || "Could not update add-on");
      }
    } catch (error) {
      message.error("Could not update add-on");
    }
    setActionKey("");
  };

  const generateApiKey = async (storeId) => {
    setActionKey(`api-key-${storeId}`);
    try {
      const res = await instance.post(
        "/fetch_or_create_enable_reseller_store_api_key/",
        {
          authToken,
          storeId,
        },
      );
      if (res.data.success) {
        syncPayload(res.data);
        message.success("API key is ready");
      } else {
        message.error(res.data.message || "Could not fetch API key");
      }
    } catch (error) {
      message.error("Could not fetch API key");
    }
    setActionKey("");
  };

  const renderBooleanAddon = (store, addon) => (
    <Row
      key={`${store.id}-${addon.key}`}
      gutter={[12, 12]}
      align="middle"
      style={{ marginBottom: 12 }}
    >
      <Col xs={24} md={10}>
        <div style={{ fontWeight: 600 }}>{addon.label}</div>
        <Text type="secondary">
          {formatMoney(addon.price, addon.currency)} / month
        </Text>
      </Col>
      <Col xs={24} md={8}>
        <Space wrap>
          {addon.enabled ? (
            <Tag color="green">Enabled</Tag>
          ) : (
            <Tag color="default">Disabled</Tag>
          )}
          {addon.enabled && addon.billed ? (
            <Tag color="blue">Billed</Tag>
          ) : null}
          {addon.enabled && !addon.billed ? (
            <Tag color="gold">Enabled, not billed</Tag>
          ) : null}
        </Space>
      </Col>
      <Col xs={24} md={6} style={{ textAlign: "right" }}>
        <Switch
          checked={addon.enabled}
          loading={actionKey === `${addon.key}-${store.id}`}
          onChange={(checked) =>
            submitAddon(store.id, addon.key, {
              enabled: checked,
            })
          }
        />
      </Col>
    </Row>
  );

  const renderBranchesAddon = (store, addon) => {
    const isUnlimited = hasUnlimitedBranches(store);
    const draftValue =
      branchDrafts[store.id] !== undefined
        ? branchDrafts[store.id]
        : addon.quantity || 0;

    return (
      <Row
        key={`${store.id}-${addon.key}`}
        gutter={[12, 12]}
        align="middle"
        style={{ marginBottom: 12 }}
      >
        <Col xs={24} md={10}>
          <div style={{ fontWeight: 600 }}>{addon.label}</div>
          <Text type="secondary">
            {formatMoney(addon.price, addon.currency)} each / month
          </Text>
        </Col>
        <Col xs={24} md={8}>
          <Space wrap>
            {isUnlimited ? (
              <Tag color="default">Unlimited branches</Tag>
            ) : (
              <Tag color={draftValue > 0 ? "green" : "default"}>
                {draftValue > 0
                  ? `${draftValue} extra branch${draftValue > 1 ? "es" : ""}`
                  : "No extra branches"}
              </Tag>
            )}
            {addon.enabled && addon.billed ? (
              <Tag color="blue">Billed</Tag>
            ) : null}
            {addon.enabled && !addon.billed ? (
              <Tag color="gold">Enabled, not billed</Tag>
            ) : null}
          </Space>
          <div style={{ marginTop: 8 }}>
            <Text type="secondary">
              Monthly charge:{" "}
              {isUnlimited
                ? "Not billable"
                : formatMoney(draftValue * addon.price, addon.currency)}
            </Text>
          </div>
        </Col>
        <Col xs={24} md={6} style={{ textAlign: "right" }}>
          <Space>
            <InputNumber
              min={0}
              value={draftValue}
              disabled={isUnlimited}
              onChange={(value) =>
                setBranchDrafts((current) => ({
                  ...current,
                  [store.id]: value || 0,
                }))
              }
            />
            <Button
              type="primary"
              loading={actionKey === `${addon.key}-${store.id}`}
              disabled={isUnlimited || draftValue === (addon.quantity || 0)}
              onClick={() =>
                submitAddon(store.id, addon.key, {
                  quantity: draftValue || 0,
                })
              }
            >
              Save
            </Button>
          </Space>
        </Col>
      </Row>
    );
  };

  const renderProductsAddon = (store, addon) => {
    const isUnlimited = hasUnlimitedProducts(store);
    const draftValue =
      productDrafts[store.id] !== undefined
        ? productDrafts[store.id]
        : addon.quantity || 0;
    const extraProducts = draftValue * PRODUCT_BLOCK_SIZE;

    return (
      <Row
        key={`${store.id}-${addon.key}`}
        gutter={[12, 12]}
        align="middle"
        style={{ marginBottom: 12 }}
      >
        <Col xs={24} md={10}>
          <div style={{ fontWeight: 600 }}>{addon.label}</div>
          <Text type="secondary">
            {formatMoney(addon.price, addon.currency)} per {PRODUCT_BLOCK_SIZE} products / month
          </Text>
        </Col>
        <Col xs={24} md={8}>
          <Space wrap>
            {isUnlimited ? (
              <Tag color="default">Unlimited products</Tag>
            ) : (
              <Tag color={draftValue > 0 ? "green" : "default"}>
                {draftValue > 0
                  ? `${extraProducts} extra products`
                  : "No extra products"}
              </Tag>
            )}
            {addon.enabled && addon.billed ? (
              <Tag color="blue">Billed</Tag>
            ) : null}
            {addon.enabled && !addon.billed ? (
              <Tag color="gold">Enabled, not billed</Tag>
            ) : null}
          </Space>
          <div style={{ marginTop: 8 }}>
            <Text type="secondary">
              Monthly charge:{" "}
              {isUnlimited
                ? "Not billable"
                : formatMoney(draftValue * addon.price, addon.currency)}
            </Text>
          </div>
        </Col>
        <Col xs={24} md={6} style={{ textAlign: "right" }}>
          <Space>
            <InputNumber
              min={0}
              value={draftValue}
              disabled={isUnlimited}
              onChange={(value) =>
                setProductDrafts((current) => ({
                  ...current,
                  [store.id]: value || 0,
                }))
              }
            />
            <Button
              type="primary"
              loading={actionKey === `${addon.key}-${store.id}`}
              disabled={isUnlimited || draftValue === (addon.quantity || 0)}
              onClick={() =>
                submitAddon(store.id, addon.key, {
                  quantity: draftValue || 0,
                })
              }
            >
              Save
            </Button>
          </Space>
        </Col>
      </Row>
    );
  };

  if (loading) {
    return (
      <div
        className="mx-auto"
        style={{ maxWidth: 920, width: "calc(100% - 66px)", marginTop: 16 }}
      >
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }

  return (
    <div
      className="mx-auto"
      style={{ maxWidth: 920, width: "calc(100% - 66px)", marginTop: 16 }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        <div style={{ textAlign: "left" }}>
          <div style={{ fontSize: 24, fontWeight: 700 }}>
            Manage subscriptions
          </div>
          <Text type="secondary">
            Billing runs at the beginning of every month.
          </Text>
        </div>
        <Space>
          <Button onClick={onBack}>Back to store creation</Button>
          <Button onClick={fetchStores}>Refresh</Button>
        </Space>
      </div>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16, textAlign: "left" }}
        message={`Projected next bill: ${formatMoney(
          payload?.totals?.next_month_total,
          payload?.totals?.currency || "QAR",
        )}`}
        description="This preview includes active billable stores and billed add-ons for the next monthly cycle."
      />

      <Row gutter={[12, 12]} align="middle" style={{ marginBottom: 16 }}>
        <Col xs={24} lg={16}>
          <Search
            allowClear
            placeholder="Search by store name or domain"
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            style={{ width: "100%" }}
          />
        </Col>
        <Col xs={24} lg={8}>
          <div
            style={{
              width: "100%",
              minHeight: 40,
              padding: "0 14px",
              border: "1px solid #d9d9d9",
              borderRadius: 6,
              background: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
            }}
          >
            <Text>{`Active stores only (${activeStoresCount}/${stores.length})`}</Text>
            <Switch checked={activeOnly} onChange={setActiveOnly} />
          </div>
        </Col>
      </Row>

      {!stores.length ? (
        <Card>
          <Empty description="No Enable stores found" />
        </Card>
      ) : null}

      {stores.length > 0 && !filteredStores.length ? (
        <Card>
          <Empty
            description={
              searchValue.trim()
                ? "No stores match your search"
                : "No active stores found"
            }
          />
        </Card>
      ) : null}

      {filteredStores.map((store) => {
        const branchesAddon = getAddonByKey(store, "additional_branches");
        const productsAddon = getAdditionalProductsAddon(store);
        const branchesAreUnlimited = hasUnlimitedBranches(store);
        const productsAreUnlimited = hasUnlimitedProducts(store);

        return (
          <Card
            key={store.id}
            style={{ marginBottom: 16, textAlign: "left" }}
            bodyStyle={{ paddingBottom: 12 }}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={16}>
                <Space wrap size={[8, 8]}>
                  <div style={{ fontSize: 20, fontWeight: 700 }}>
                    {store.name}
                  </div>
                  <Tag color={STATUS_COLORS[store.status] || "default"}>
                    {store.status}
                  </Tag>
                  {store.will_bill_next_month ? (
                    <Tag color="blue">Will bill next month</Tag>
                  ) : (
                    <Tag color="default">Not billed next month</Tag>
                  )}
                </Space>
                <Paragraph style={{ marginBottom: 8 }}>
                  <Text copyable>{store.domain}</Text>
                </Paragraph>
                <Row gutter={[16, 8]}>
                  <Col xs={24} md={8}>
                    <Text type="secondary">Created</Text>
                    <div>{formatDate(store.created)}</div>
                  </Col>
                  <Col xs={24} md={8}>
                    <Text type="secondary">Store fee</Text>
                    <div>{formatMoney(store.fee, store.currency)}</div>
                  </Col>
                  <Col xs={24} md={8}>
                    <Text type="secondary">Next month total</Text>
                    <div>
                      {formatMoney(store.next_month_total, store.currency)}
                    </div>
                  </Col>
                </Row>
                <Row gutter={[16, 8]} style={{ marginTop: 12 }}>
                  <Col xs={24} md={8}>
                    <Text type="secondary">Branches</Text>
                    <div>{store.branch_limit || "Unlimited"}</div>
                  </Col>
                  <Col xs={24} md={8}>
                    <Text type="secondary">Users</Text>
                    <div>{store.limit_users || "Unlimited"}</div>
                  </Col>
                  <Col xs={24} md={8}>
                    <Text type="secondary">Products</Text>
                    <div>{store.limit_products || "Unlimited"}</div>
                  </Col>
                </Row>
              </Col>
              <Col xs={24} lg={8} style={{ textAlign: "right" }}>
                <Button
                  danger={store.subscription_enabled}
                  type={store.subscription_enabled ? "default" : "primary"}
                  loading={actionKey === `subscription-${store.id}`}
                  onClick={() =>
                    submitStoreStatus(store.id, !store.subscription_enabled)
                  }
                >
                  {store.subscription_enabled
                    ? "Disable store"
                    : "Enable store"}
                </Button>
              </Col>
            </Row>

            <Divider style={{ marginTop: 18, marginBottom: 18 }} />

            <div style={{ fontWeight: 700, marginBottom: 12 }}>Features</div>
            {(store.addons || [])
              .filter((addon) => !addon.quantity_based)
              .map((addon) => renderBooleanAddon(store, addon))}

            <Divider style={{ marginTop: 18, marginBottom: 18 }} />

            <div
              style={{
                opacity: branchesAreUnlimited ? 0.5 : 1,
                background: branchesAreUnlimited ? "#fafafa" : "transparent",
                border: branchesAreUnlimited ? "1px solid #f0f0f0" : "none",
                borderRadius: 8,
                padding: branchesAreUnlimited ? 12 : 0,
                marginBottom: branchesAreUnlimited ? 12 : 0,
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 12 }}>
                Additional branches
              </div>
              {branchesAddon ? renderBranchesAddon(store, branchesAddon) : null}
            </div>

            <Divider style={{ marginTop: 18, marginBottom: 18 }} />

            <div
              style={{
                opacity: productsAreUnlimited ? 0.5 : 1,
                background: productsAreUnlimited ? "#fafafa" : "transparent",
                border: productsAreUnlimited ? "1px solid #f0f0f0" : "none",
                borderRadius: 8,
                padding: productsAreUnlimited ? 12 : 0,
                marginBottom: productsAreUnlimited ? 12 : 0,
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 12 }}>
                Additional products
              </div>
              {productsAddon ? renderProductsAddon(store, productsAddon) : null}
            </div>

            <Divider style={{ marginTop: 18, marginBottom: 18 }} />

            <div style={{ fontWeight: 700, marginBottom: 12 }}>
              Store API key
            </div>
            {store.api_key ? (
              <Paragraph
                copyable={{ text: store.api_key }}
                style={{ marginBottom: 0 }}
              >
                <Text code>{store.api_key}</Text>
              </Paragraph>
            ) : (
              <Space direction="vertical" size={8}>
                <Text type="secondary">
                  No backend API key exists yet for this store.
                </Text>
                <Button
                  type="primary"
                  loading={actionKey === `api-key-${store.id}`}
                  onClick={() => generateApiKey(store.id)}
                >
                  Generate API key
                </Button>
              </Space>
            )}
          </Card>
        );
      })}
    </div>
  );
};

export default ManageSubscriptions;
